/**
 * Main form fields for SaveFilterModal (name, color, dates, tabs, share).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { Input } from '@/components/ui';
import { ColorPicker } from '@/components/ui';

import type { StatsDateRangePreset } from '@/components/date-range';
import { UserSharePicker } from '../UserSharePicker';
import { SaveFilterDateRangeSection } from './SaveFilterDateRangeSection';

const TAB_OPTIONS = [
  { key: 'drops', label: 'Drops' },
  { key: 'lists', label: 'Lists' },
  { key: 'gateways', label: 'Gateways' },
  { key: 'sources', label: 'Sources' },
  { key: 'phone', label: 'Phone' },
  { key: 'texts', label: 'Texts' },
  { key: 'paths', label: 'Paths' },
  { key: 'landers', label: 'Landers' },
  { key: 'offers', label: 'Offers' },
] as const;

interface SaveFilterModalMainFieldsProps {
  name: string;
  onNameChange: (v: string) => void;
  color: string;
  onColorChange: (v: string) => void;
  tabs: string[];
  onToggleTab: (key: string) => void;
  shareWith: Set<string>;
  onToggleUser: (id: string) => void;
  inferredRangePreset: StatsDateRangePreset;
  includeRolling: boolean;
  onIncludeRollingChange: (v: boolean) => void;
  includeFixed: boolean;
  onIncludeFixedChange: (v: boolean) => void;
  untilToday: boolean;
  onUntilTodayChange: (v: boolean) => void;
  currentDateRangeDmy: { from: string; to: string };
  todayDmy: string;
}

export const SaveFilterModalMainFields: FC<SaveFilterModalMainFieldsProps> = ({
  name,
  onNameChange,
  color,
  onColorChange,
  tabs,
  onToggleTab,
  shareWith,
  onToggleUser,
  inferredRangePreset,
  includeRolling,
  onIncludeRollingChange,
  includeFixed,
  onIncludeFixedChange,
  untilToday,
  onUntilTodayChange,
  currentDateRangeDmy,
  todayDmy,
}) => (
  <div className="border-t px-5 py-4">
    <div className="flex gap-3">
      <div className="flex-1">
        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Name
        </label>
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. High ROI filter"
          className="h-9 text-sm"
        />
      </div>
      <div className="w-[140px] shrink-0">
        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Color
        </label>
        <ColorPicker
          color={color}
          onChange={onColorChange}
          placeholder="#3b82f6"
          className="h-9"
        />
      </div>
    </div>

    <SaveFilterDateRangeSection
      inferredRangePreset={inferredRangePreset}
      includeRolling={includeRolling}
      onIncludeRollingChange={onIncludeRollingChange}
      includeFixed={includeFixed}
      onIncludeFixedChange={onIncludeFixedChange}
      untilToday={untilToday}
      onUntilTodayChange={onUntilTodayChange}
      previewFrom={currentDateRangeDmy.from}
      previewToFixed={currentDateRangeDmy.to}
      todayDmy={todayDmy}
    />

    <div className="mt-4 grid grid-cols-[1fr_1fr] gap-5">
      <div>
        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Visible in
        </label>
        <div className="flex flex-wrap gap-1">
          {TAB_OPTIONS.map(({ key, label }) => {
            const on = tabs.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggleTab(key)}
                className={`rounded-md border px-2 py-[3px] text-[11px] font-medium transition-colors ${
                  on
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-transparent bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Share with
        </label>
        <UserSharePicker selected={shareWith} onToggle={onToggleUser} />
      </div>
    </div>
  </div>
);
